import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores';
import PinAuthLayer from '../components/PinAuthLayer';
import { isDeviceTrusted } from '../utils/deviceTrust';
import AuthHeader from '../components/AuthHeader';
import { colors, radius, sheen, spacing, elevation } from '../theme';
import {
  MAPS_APPS,
  MAPS_APP_ORDER,
  isIOS,
  getPreferredMapsApp,
  setPreferredMapsApp,
  buildMapsAppUrl,
} from '../utils/mapsApp';

const IS_IOS = isIOS();

const MapViewer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [routeDistanceMiles, setRouteDistanceMiles] = useState(null);
  const [routeStatusMessage, setRouteStatusMessage] = useState("");
  const [routeLoading, setRouteLoading] = useState(false);
  const mapInstanceRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);

  // Get address or coordinates from navigation state
  const { address, customerName, lat, lng, bookingId } = location.state || {};
  
  // Also check URL params for coordinates (for breakdown reports)
  const urlParams = new URLSearchParams(window.location.search);
  const urlLat = urlParams.get('lat');
  const urlLng = urlParams.get('lng');
  const urlBookingId = urlParams.get('bookingId');
  
  // Determine the actual bookingId to use
  const actualBookingId = bookingId || urlBookingId;
  
  const coordinates = lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : 
                     urlLat && urlLng ? { lat: parseFloat(urlLat), lng: parseFloat(urlLng) } : null;

  // Hand-off to the phone's maps app. iPhone drivers pick an app once; the
  // choice lives in localStorage on that phone. Everyone else gets Google Maps.
  const mapsDestination =
    typeof address === "string" && address.trim()
      ? { address: address.trim() }
      : coordinates
      ? { lat: coordinates.lat, lng: coordinates.lng }
      : null;
  const [preferredMapsApp, setPreferredMapsAppState] = useState(() => getPreferredMapsApp());
  const [showMapsAppPicker, setShowMapsAppPicker] = useState(false);

  const launchMapsApp = (app) => {
    const url = buildMapsAppUrl(app, mapsDestination);
    if (!url) return;
    // Stays synchronous inside the press handler: iOS blocks popups opened after an await.
    window.open(url, "_blank");
  };

  const handleMapsAppPress = () => {
    if (!mapsDestination) return;
    if (!IS_IOS) return launchMapsApp("google");
    if (preferredMapsApp) return launchMapsApp(preferredMapsApp);
    setShowMapsAppPicker(true);
  };

  const handlePickMapsApp = (app) => {
    setPreferredMapsApp(app);
    setPreferredMapsAppState(app);
    setShowMapsAppPicker(false);
    launchMapsApp(app);
  };

  const GOOGLE_MAPS_API_KEY = "AIzaSyDnMrIVkx-6V0Brm4pxezeOVcTOELsTmdQ";

  // Load Google Maps script
  useEffect(() => {
    console.log("🗺️ MapViewer mounted, checking Google Maps...");
    console.log("🌍 Current URL:", window.location.href);
    console.log("🔑 API Key:", GOOGLE_MAPS_API_KEY);

    // Check if script already loaded
    if (window.google && window.google.maps) {
      console.log("✅ Google Maps already loaded");
      setMapLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com"]`
    );
    if (existingScript) {
      console.log("⏳ Google Maps script already loading, waiting...");

      // Wait for existing script to load
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps) {
          console.log("✅ Google Maps loaded from existing script");
          setMapLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkLoaded);
        if (!window.google || !window.google.maps) {
          console.error("❌ Google Maps script timeout");
          setError("Maps failed to load (timeout)");
        }
      }, 10000);

      return;
    }

    console.log("📥 Loading Google Maps script...");
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log("✅ Google Maps API loaded successfully");
      if (window.google && window.google.maps) {
        setMapLoaded(true);
      } else {
        console.error("❌ Script loaded but google.maps not available");
        setError("Maps API loaded but not initialized");
      }
    };
    script.onerror = (e) => {
      console.error("❌ Failed to load Google Maps API", e);
      setError("Failed to load Google Maps - Check API key and network");
      setMapLoaded(false);
    };

    document.head.appendChild(script);

    // Timeout failsafe
    setTimeout(() => {
      if (!window.google || !window.google.maps) {
        console.error("❌ Google Maps load timeout");
        setError("Maps failed to load after 15s");
      }
    }, 15000);
  }, []);

  // Show PIN auth layer for unauthenticated users
  if (!isAuthenticated && !isVerified && actualBookingId) {
    return (
      <View style={{ flex: 1, backgroundColor: "#F6F8F7" }}>
        <AuthHeader
          title="Map Viewer"
          subtitle=""
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={{}}
          storeLogo=""
        />
        <PinAuthLayer
          onAuthenticated={() => {
            console.log("✅ PIN verified for MapViewer:", actualBookingId);
            setIsVerified(true);
          }}
          bookingId={actualBookingId}
        />
      </View>
    );
  }

  // Initialize map when loaded
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || (!address && !coordinates)) {
      console.log("⏳ Waiting for map initialization...", {
        mapLoaded,
        hasMapRef: !!mapRef.current,
        address,
        coordinates,
      });
      return;
    }

    // If we have coordinates, use them directly
    if (coordinates) {
      console.log("🗺️ Initializing map with coordinates:", coordinates);
      const location = new window.google.maps.LatLng(coordinates.lat, coordinates.lng);
      initializeMapWithLocation(location, coordinates.lat, coordinates.lng);
      return;
    }

    // Otherwise, geocode the address
    console.log("🗺️ Initializing map with address:", address);
    console.log("🗺️ Map ref element:", mapRef.current);
    console.log("🗺️ Window size:", window.innerWidth, "x", window.innerHeight);

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: address }, (results, status) => {
      console.log("📍 Geocoding result:", status);

      if (status === "OK") {
        const location = results[0].geometry.location;
        console.log("✅ Location found:", location.toString());
        
        initializeMapWithLocation(location, location.lat(), location.lng());
      } else {
        console.error("❌ Geocoding failed:", status);
        setError(`Could not find location: ${address}`);
      }
    });
  }, [mapLoaded, address, coordinates]);

  const requestRoute = (origin, destinationLatLng) => {
    if (!directionsServiceRef.current || !directionsRendererRef.current) {
      return;
    }

    const mapInstance = mapInstanceRef.current;
    if (!mapInstance) {
      return;
    }

    directionsRendererRef.current.setMap(mapInstance);

    directionsServiceRef.current.route(
      {
        origin,
        destination: destinationLatLng,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (response, status) => {
        setRouteLoading(false);
        if (status !== "OK") {
          console.warn("⚠️ Directions request failed:", status);
          setRouteStatusMessage("Route unavailable");
          return;
        }

        directionsRendererRef.current.setDirections(response);

        const leg = response.routes?.[0]?.legs?.[0];
        if (leg?.distance?.value) {
          const miles = leg.distance.value * 0.000621371;
          setRouteDistanceMiles(miles);
          setRouteStatusMessage(`${leg.distance.text} via ${leg.summary || "route"}`);
        }
      }
    );
  };

  const initializeMapWithLocation = (location, lat, lng) => {
    if (!mapRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: location,
      zoom: 15,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      zoomControl: true,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "poi.business",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "transit",
          elementType: "labels.icon",
          stylers: [{ visibility: "off" }],
        },
      ],
    });

    mapInstanceRef.current = map;
    directionsServiceRef.current = new window.google.maps.DirectionsService();
    directionsRendererRef.current = new window.google.maps.DirectionsRenderer({ map });
    directionsRendererRef.current.setOptions({ suppressMarkers: true });

    console.log("🗺️ Map object created:", map);
    console.log(
      "🗺️ Map div dimensions:",
      mapRef.current.offsetWidth,
      "x",
      mapRef.current.offsetHeight
    );

    // Force map to resize after creation
    setTimeout(() => {
      window.google.maps.event.trigger(map, "resize");
      map.setCenter(location);
      console.log("🔄 Map resized and recentered");
    }, 100);

    // Add marker for the location
    const markerTitle = coordinates 
      ? (customerName || `Breakdown Location (${lat.toFixed(6)}, ${lng.toFixed(6)})`)
      : (customerName || address);
    
    new window.google.maps.Marker({
      position: location,
      map: map,
      title: markerTitle,
      animation: window.google.maps.Animation.DROP,
      icon: coordinates ? {
        url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
      } : undefined,
    });

    // Add home base marker (only if not showing breakdown location)
    if (!coordinates) {
      const homeGeocoder = new window.google.maps.Geocoder();
      homeGeocoder.geocode(
        { address: "9102 Bonita Beach Rd SE, Bonita Springs, FL 34135" },
        (results, status) => {
          if (status === "OK") {
            new window.google.maps.Marker({
              position: results[0].geometry.location,
              map: map,
              title: "Bonita Bikes - Home Base",
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 10,
                fillColor: "#1F8A5B",
                fillOpacity: 1,
                strokeColor: "#FFFFFF",
                strokeWeight: 2,
              },
              label: {
                text: "🏠",
                fontSize: "18px",
              },
            });
          }
        }
      );
    }

    // Add blue marker for user's current location
    if (navigator.geolocation) {
    setRouteLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        requestRoute(userLocation, location);

        new window.google.maps.Marker({
          position: userLocation,
          map: map,
          title: "Your Location",
          icon: {
            url: "http://maps.google.com/mapfiles/kml/shapes/cabs.png",
            scaledSize: new window.google.maps.Size(32, 32),
          },
        });

        // Calculate distance between address and user location (simple haversine approximation)
        const toRad = (deg) => deg * (Math.PI / 180);
        const R = 6371000; // Earth radius in meters
        const dLat = toRad(userLocation.lat - location.lat());
        const dLng = toRad(userLocation.lng - location.lng());
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRad(location.lat())) *
            Math.cos(toRad(userLocation.lat)) *
            Math.sin(dLng / 2) *
            Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c; // Distance in meters

        // Only adjust bounds if user is within 50km (about 31 miles) of the address
        // Otherwise, keep zoom at 16 centered on the address
        const maxDistanceForFitBounds = 50000; // 50km in meters

        if (distance < maxDistanceForFitBounds) {
          const bounds = new window.google.maps.LatLngBounds();
          bounds.extend(location);
          bounds.extend(new window.google.maps.LatLng(userLocation.lat, userLocation.lng));

          map.fitBounds(bounds);

          setTimeout(() => {
            if (map.getZoom() < 12) {
              map.setZoom(15);
              map.setCenter(location);
            }
          }, 100);
        } else {
          map.setZoom(15);
          map.setCenter(location);
        }

        console.log("✅ Added user location marker");
      },
      (error) => {
        console.warn("Could not get user location:", error.message);
        setRouteLoading(false);
      }
    );
    }
    else {
      setRouteLoading(false);
    }

    console.log("✅ Map initialized with location:", coordinates ? "coordinates" : "address");
  };

  if (!address && !coordinates) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>No address or coordinates provided</Text>
        </View>
      </View>
    );
  }

  console.log("🎬 MapViewer rendering:", {
    mapLoaded,
    error,
    address,
    coordinates,
    customerName,
  });

  return (
    <View style={styles.container}>
      {/* Map Container */}
      <View style={styles.mapContainerFullscreen}>
        {error ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>❌ {error}</Text>
            <Text style={styles.errorSubtext}>Address: {address}</Text>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.retryButton}
              onPress={() => {
                setError("");
                setMapLoaded(false);
                window.location.reload();
              }}
            >
              <Text style={styles.retryButtonText}>🔄 Retry</Text>
            </TouchableOpacity>
          </View>
        ) : !mapLoaded ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#0E7C6B" />
            <Text style={styles.loadingText}>Loading map...</Text>
              <Text
                style={styles.loadingText}
              >
                Debug: mapLoaded={String(mapLoaded)}, error={error || "none"}
              </Text>
          </View>
        ) : (
          <>
            <div
              ref={mapRef}
              style={{
                width: "100%",
                height: "100vh",
                backgroundColor: "#DDE3E2",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            />
            {/* Floating Back Button */}
            <View style={styles.floatingBackButtonContainer}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.floatingBackButton}
                onPress={() => navigate(-1)}
              >
                <Text style={styles.floatingBackButtonText}>←</Text>
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
      {/* Hands the drive to the phone's maps app: turn-by-turn, voice, rerouting. */}
      {mapsDestination && (
        <View style={styles.mapsAppButtonContainer}>
          <TouchableOpacity
            dataSet={{ hover: "lift" }}
            style={styles.mapsAppButton}
            onPress={handleMapsAppPress}
            activeOpacity={0.9}
          >
            <Text style={styles.mapsAppButtonIcon}>🧭</Text>
            <Text style={styles.mapsAppButtonText}>Maps App</Text>
          </TouchableOpacity>
          {IS_IOS && preferredMapsApp && (
            <TouchableOpacity
              style={styles.changeMapsAppLink}
              onPress={() => setShowMapsAppPicker(true)}
            >
              <Text style={styles.changeMapsAppLinkText}>
                {MAPS_APPS[preferredMapsApp].label} · Change
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )}
      {showMapsAppPicker && (
        <View style={styles.mapsAppPickerScrim}>
          <TouchableOpacity
            style={StyleSheet.absoluteFill}
            activeOpacity={1}
            onPress={() => setShowMapsAppPicker(false)}
          />
          <View style={styles.mapsAppPickerSheet}>
            <Text style={styles.mapsAppPickerTitle}>Open directions in…</Text>
            <Text style={styles.mapsAppPickerSubtitle}>
              Remembered on this phone. Change it any time under the button.
            </Text>
            {MAPS_APP_ORDER.map((app) => {
              const selected = app === preferredMapsApp;
              return (
                <TouchableOpacity
                  key={app}
                  dataSet={{ hover: "lift" }}
                  style={[styles.mapsAppOption, selected && styles.mapsAppOptionSelected]}
                  onPress={() => handlePickMapsApp(app)}
                  activeOpacity={0.9}
                >
                  <Text style={[styles.mapsAppOptionText, selected && styles.mapsAppOptionTextSelected]}>
                    {MAPS_APPS[app].label}
                  </Text>
                </TouchableOpacity>
              );
            })}
            <TouchableOpacity
              style={styles.mapsAppPickerCancel}
              onPress={() => setShowMapsAppPicker(false)}
            >
              <Text style={styles.mapsAppPickerCancelText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
      {routeLoading && (
        <View style={styles.routeLoadingOverlay}>
          <ActivityIndicator size="large" color="#FFFFFF" />
          <Text style={styles.routeLoadingText}>Calculating route...</Text>
        </View>
      )}
      {routeDistanceMiles !== null && (
        <>
          <View style={styles.routeOverlayTop}>
            <Text style={styles.routeOverlayText}>
              {routeDistanceMiles.toFixed(1)} mi to destination
            </Text>
          </View>
          <View style={styles.routeInfoContainer}>
            <Text style={styles.routeInfoText}>
              Route distance: {routeDistanceMiles.toFixed(1)} mi
            </Text>
            <Text style={styles.routeInfoSubtext}>
              {routeStatusMessage || "Route displayed between your location and destination"}
            </Text>
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#F6F8F7",
  },
  topNav: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#FFFFFF",
    paddingVertical: 15,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  backButton: {
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  backButtonText: {
    fontSize: 24,
    color: "#0E7C6B",
    fontWeight: "bold",
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  headerSpacer: {
    width: 44,
  },
  infoCard: {
    backgroundColor: "#FFFFFF",
    marginHorizontal: 16,
    marginTop: 16,
    padding: 16,
    borderRadius: 8,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  customerName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
  },
  addressText: {
    fontSize: 15,
    color: "#0E7C6B",
    lineHeight: 22,
  },
  errorBanner: {
    backgroundColor: "#FDF3E3",
    padding: 12,
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  errorBannerText: {
    color: "#7A4E12",
    fontSize: 14,
  },
  mapContainerFullscreen: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#DDE3E2",
  },
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },
  loadingText: {
    fontSize: 16,
    color: "#5C6B68",
  },
  actionButtons: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 12,
  },
  navigateButton: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  navigateButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  errorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  errorText: {
    fontSize: 18,
    color: "#C4453D",
    marginBottom: 8,
    textAlign: "center",
  },
  errorSubtext: {
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 16,
    textAlign: "center",
    paddingHorizontal: 20,
  },
  retryButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    marginTop: 12,
  },
  retryButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  actionButtons: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 12,
  },
  navigateButton: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  navigateButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  errorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  errorText: {
    fontSize: 18,
    color: "#C4453D",
    marginBottom: 8,
    textAlign: "center",
  },
  errorSubtext: {
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 16,
    textAlign: "center",
    paddingHorizontal: 20,
  },
  retryButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    marginTop: 12,
  },
  retryButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  mapsAppButtonContainer: {
    position: "absolute",
    bottom: 12,
    left: 90, // clears the 50px floating back button at left: 20
    right: 20,
    zIndex: 10003, // above the "Calculating route" overlay so it is never blocked
  },
  mapsAppButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 56,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: radius.md,
    backgroundColor: colors.primary,
    // Same recipe as the primary Button: teal-tinted glow plus a top sheen.
    boxShadow: `0 1px 2px rgba(15, 26, 25, 0.10), 0 4px 12px rgba(14, 124, 107, 0.28), ${sheen.onDark}`,
    elevation: 6,
    cursor: "pointer",
  },
  mapsAppButtonIcon: {
    fontSize: 24,
    marginRight: 10,
  },
  mapsAppButtonText: {
    color: colors.surface,
    fontSize: 20,
    fontWeight: "700",
    letterSpacing: 0.3,
  },
  changeMapsAppLink: {
    alignSelf: "center",
    marginTop: 6,
    paddingVertical: 3,
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: colors.overlay,
  },
  changeMapsAppLinkText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: "600",
  },
  mapsAppPickerScrim: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "flex-end",
    backgroundColor: colors.overlay,
    zIndex: 10005,
  },
  mapsAppPickerSheet: {
    backgroundColor: colors.surface,
    borderTopLeftRadius: radius.lg,
    borderTopRightRadius: radius.lg,
    padding: spacing.lg,
    paddingBottom: spacing.xl,
    boxShadow: elevation.xl,
    elevation: 12,
  },
  mapsAppPickerTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: colors.ink900,
    textAlign: "center",
    marginBottom: spacing.xs,
  },
  mapsAppPickerSubtitle: {
    fontSize: 13,
    color: colors.ink500,
    textAlign: "center",
    marginBottom: spacing.lg,
  },
  mapsAppOption: {
    alignItems: "center",
    paddingVertical: spacing.lg,
    marginBottom: spacing.sm,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surfaceMuted,
    cursor: "pointer",
  },
  mapsAppOptionSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primarySoft,
  },
  mapsAppOptionText: {
    fontSize: 18,
    fontWeight: "600",
    color: colors.ink700,
  },
  mapsAppOptionTextSelected: {
    color: colors.primaryDark,
  },
  mapsAppPickerCancel: {
    alignItems: "center",
    paddingVertical: spacing.md,
    marginTop: spacing.xs,
    cursor: "pointer",
  },
  mapsAppPickerCancelText: {
    fontSize: 16,
    fontWeight: "600",
    color: colors.primary,
  },
  floatingBackButtonContainer: {
    position: 'absolute',
    bottom: 20,
    left: 20,
    zIndex: 10000,
  },
  floatingBackButton: {
    width: 50,
    height: 50,
    backgroundColor: 'rgba(0, 122, 255, 0.5)',
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 10,
  },
  floatingBackButtonText: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
    lineHeight: 28,
  },
  routeInfoContainer: {
    position: "absolute",
    bottom: 104,
    left: 20,
    right: 20,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "rgba(0,0,0,0.6)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10001,
  },
  routeInfoText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  routeInfoSubtext: {
    fontSize: 13,
    color: "#EDF1F0",
    marginTop: 2,
  },
  routeLoadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10002,
  },
  routeLoadingText: {
    marginTop: 8,
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  routeOverlayTop: {
    position: "absolute",
    top: 20,
    left: "50%",
    transform: [{ translateX: -75 }],
    minWidth: 150,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: "rgba(0,0,0,0.6)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10001,
  },
  routeOverlayText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  floatingBackButtonText: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
    lineHeight: 28,
  },
});

export default MapViewer;

